import { TFunction } from 'i18next';
import {
  IncentiveStatementEquipmentCategory,
  IncentiveStatementFacility,
  IncentiveStatementFinancials,
  IncentiveStatementProgram,
  IncentiveStatementResponse,
  TCountryId,
  normalizeIncentiveStatementResponse,
} from '/src/lib/models';
import { CountryId } from '/src/lib/types';
import { formatNumber } from '/src/utils';

type CsvRow = string[];

type CsvBuildOptions = {
  incentiveStatement: IncentiveStatementResponse;
  isSummary?: boolean;
  countryId?: TCountryId;
  t: TFunction;
};

const formatAmount = (value: string | number | null | undefined, decimals: number) => {
  const parsed = parseFloat(String(value ?? ''));
  return Number.isNaN(parsed) ? '-' : formatNumber(value ?? '0', true, false, false, decimals);
};

const formatCost = (value: string | number | null | undefined, decimals: number) => {
  const formatted = formatAmount(value, decimals);
  return formatted === '-' ? '-' : `(${formatted})`;
};

const formatCreditPrice = (value: string | number | null | undefined) => {
  const parsed = parseFloat(String(value ?? ''));
  return Number.isNaN(parsed) ? 'N/A' : formatNumber(value ?? '0', true, false, false, 2);
};

const escapeCsvValue = (value: string) => {
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const buildCsvString = (rows: CsvRow[]) =>
  rows.map((row) => row.map((value) => escapeCsvValue(value)).join(',')).join('\n');

const buildDataRowValues = (params: {
  programData?: IncentiveStatementProgram;
  facilityData?: IncentiveStatementFacility;
  categoryData?: IncentiveStatementEquipmentCategory;
  totalsData?: IncentiveStatementFinancials;
  t: TFunction;
}) => {
  const { programData, facilityData, categoryData, totalsData, t } = params;
  if (!facilityData && !totalsData && !programData && !categoryData) return null;

  const values: Record<string, string> = {};

  values.title = programData
    ? programData.name
    : facilityData
      ? facilityData.address
      : categoryData
        ? categoryData.name
        : t('statements:totals');

  const credits = programData?.credits ?? facilityData?.credits ?? categoryData?.credits ?? totalsData?.credits;
  values.credits = formatAmount(credits, 2);

  const creditPrice = programData?.credit_price ?? facilityData?.credit_price ?? categoryData?.credit_price;
  values.credit_price = formatCreditPrice(creditPrice);

  const totalValue =
    programData?.total_value ?? facilityData?.total_value ?? categoryData?.total_value ?? totalsData?.total_value;
  values.total_value = formatAmount(totalValue, 2);

  const recCost = programData?.rec_cost ?? facilityData?.rec_cost ?? categoryData?.rec_cost ?? totalsData?.rec_cost;
  values.rec_cost = formatCost(recCost, 2);

  const taxesOnCreditSale =
    programData?.taxes_on_credit_sale ??
    facilityData?.taxes_on_credit_sale ??
    categoryData?.taxes_on_credit_sale ??
    totalsData?.taxes_on_credit_sale;
  values.taxes_on_credit_sale = formatAmount(taxesOnCreditSale, 2);

  const brokerCost =
    programData?.broker_cost ?? facilityData?.broker_cost ?? categoryData?.broker_cost ?? totalsData?.broker_cost;
  values.broker_cost = formatCost(brokerCost, 2);

  const taxesOnServices =
    programData?.taxes_on_services ??
    facilityData?.taxes_on_services ??
    categoryData?.taxes_on_services ??
    totalsData?.taxes_on_services;
  values.taxes_on_services = formatCost(taxesOnServices, 2);

  const fuseServiceFee =
    programData?.fuse_service_fee ??
    facilityData?.fuse_service_fee ??
    categoryData?.fuse_service_fee ??
    totalsData?.fuse_service_fee;
  values.fuse_service_fee = formatCost(fuseServiceFee, 2);

  const totalKwh =
    programData?.total_kwh ?? facilityData?.total_kwh ?? categoryData?.total_kwh ?? totalsData?.total_kwh;
  values.total_kwh = formatAmount(totalKwh, 0);

  const netIncentive =
    programData?.net_incentive ??
    facilityData?.net_incentive ??
    categoryData?.net_incentive ??
    totalsData?.net_incentive;
  values.net_incentive = formatAmount(netIncentive, 2);

  return values;
};

export const buildIncentiveStatementCsvRows = (options: CsvBuildOptions) => {
  const { incentiveStatement, isSummary, countryId, t } = options;
  const normalized = normalizeIncentiveStatementResponse(incentiveStatement);

  const infoRows: CsvRow[] = [
    [t('common:clientName'), normalized.client_name],
    [t('common:reportingPeriod'), normalized.reporting_period],
    [],
  ];

  const isCanada = countryId === CountryId.Canada;

  const headers = [
    t('common:program'),
    t('common:totalKwh'),
    t('statements:credits'),
    t('statements:creditPrice'),
    t('statements:totalValue'),
    isCanada ? t('statements:taxesOnCreditSale') : t('statements:recCost'),
    isCanada ? t('statements:taxesOnServices') : t('statements:brokerCost'),
    t('statements:fuseServiceFee'),
    t('statements:netIncentive'),
  ];

  const buildRow = (values: Record<string, string>): CsvRow => [
    values.title,
    values.total_kwh,
    values.credits,
    values.credit_price,
    values.total_value,
    isCanada ? values.taxes_on_credit_sale : values.rec_cost,
    isCanada ? values.taxes_on_services : values.broker_cost,
    values.fuse_service_fee,
    values.net_incentive,
  ];

  const dataRows: CsvRow[] = [];
  normalized.programs.forEach((program) => {
    const programRow = buildDataRowValues({ programData: program, t });
    if (programRow) dataRows.push(buildRow(programRow));

    if (!isSummary) {
      program.facilities.forEach((facility) => {
        const facilityRow = buildDataRowValues({ facilityData: facility, t });
        if (facilityRow) dataRows.push(buildRow(facilityRow));

        facility.equipment_categories?.forEach((category) => {
          const categoryRow = buildDataRowValues({ categoryData: category, t });
          if (categoryRow) dataRows.push(buildRow(categoryRow));
        });
      });
    }
  });

  const totalsRow = buildDataRowValues({ totalsData: normalized.totals, t });
  if (totalsRow) dataRows.push(buildRow(totalsRow));

  dataRows.push([
    t('statements:grandTotal'),
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    formatAmount(normalized.totals.net_incentive, 2),
  ]);

  return [...infoRows, headers, ...dataRows];
};

export const downloadCsv = (rows: CsvRow[], filename: string) => {
  const csvString = buildCsvString(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
